import { useEditor } from "@grapesjs/react";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import type { Component } from "grapesjs";
import { useCallback, useEffect, useState } from "react";

export const DYNAMIC_VAR_OPTIONS = [
  { value: "", label: "None" },
  { value: "product_name", label: "Product name" },
  { value: "product_price", label: "Product price" },
  { value: "product_image", label: "Product image" },
] as const;

export const DATA_INC_VAR_ATTR = "data-inc-var";
const ATTR_NAME = DATA_INC_VAR_ATTR;

function LightningLanderVariableMapping() {
  const editor = useEditor();
  const [selectedComponent, setSelectedComponent] = useState<Component | null>(
    null,
  );
  const [value, setValue] = useState<string>(DYNAMIC_VAR_OPTIONS[0].value);

  useEffect(() => {
    if (!editor) return;
    const sync = () => {
      const sel = editor.getSelected();
      setSelectedComponent(sel ?? null);
      if (sel) {
        const attrs = sel.get("attributes") || {};
        setValue(attrs[ATTR_NAME] || "");
      } else {
        setValue("");
      }
    };
    sync();
    editor.on("component:select", sync);
    editor.on("component:update", sync);
    return () => {
      editor.off("component:select", sync);
      editor.off("component:update", sync);
    };
  }, [editor]);

  const handleChange = useCallback(
    (newValue: string) => {
      if (!selectedComponent) return;
      setValue(newValue);
      if (newValue) {
        selectedComponent.addAttributes({ [ATTR_NAME]: newValue });
      } else {
        selectedComponent.removeAttributes(ATTR_NAME);
      }
    },
    [selectedComponent],
  );

  if (!editor || !selectedComponent) return null;

  return (
    <Box p={2} pb={0}>
      <Typography variant="subtitle2" color="text.secondary">
        Dynamic block variable
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Select a variable to map to the selected element. This can be used in
        the landing page builder to dynamically update the element.
      </Typography>
      <TextField
        select
        label="Dynamic Variable"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        fullWidth
        size="small"
      >
        {DYNAMIC_VAR_OPTIONS.map((opt) => (
          <MenuItem key={opt.value || "none"} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
}

export default LightningLanderVariableMapping;
